import { useEffect, useMemo, useState } from 'react';
import Raven from 'raven-js';
import { readUnhandledEnumCase } from 'rpc-client-utils';
import styled from 'foundations-theming/utils';
import { sanitize } from 'sanitize-text/sanitizers/Sanitize';
import { config as sanitizerConfig } from 'sanitize-text/sanitizers/SanitizeConfiguration';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { Button } from 'foundations-components/transitional/components/Button';
import { ExpandableText } from 'foundations-components/transitional/components/ExpandableText';
import { Flex } from 'foundations-components/components/Flex';
import { Text } from 'foundations-components/components/Text';
import { View } from 'foundations-components/transitional/components/View';
import { CardWrapper } from 'foundations-components/transitional/components/Card';
import { H7 } from 'foundations-components/transitional/components/H7';
import { Tag } from 'foundations-components/transitional/components/Tag';
import { DownIcon } from 'foundations-assets/icons/components/DownIcon';
import { UpIcon } from 'foundations-assets/icons/components/UpIcon';
import { useMediaQuery } from '../../../utils/Styling';
import { SkeletonWrapper } from '../../../components/Skeletons/subcomponents/Skeleton';
import { OverviewContent } from '../components/OverviewContent';
import SectionHeading from '../../../components/SectionHeading';
import InstructorList from '../../../components/list/InstructorList';
import UsageTracker from '../../../utils/UsageTracker';
import { ModalTileSection } from '../components/ModalTileSection';
import { useEventInstructors, useEventRecording, useLearningEvent } from '../../liveEvents/liveEventQueries';
import { MYLEARNING_URL_FRAGMENT } from '../../../routes/academyRoutePaths';
import { LiveEventRegistrationAlert } from '../../liveEvents/LiveEventRegistrationAlert';
import { getPastSessions, getPriorityJoinableSession, getSessionStartTime, getUpcomingSessionsNotStartingSoon } from '../../liveEvents/sessionTimeUtils';
import { SessionCard } from '../../liveEvents/components/SessionCard';
import { RegistrationConfirmationModal } from '../../liveEvents/RegistrationConfirmationModal';
import { LANGUAGE_STRINGS } from '../../../constants/languageStrings';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const INITIAL_SESSIONS_SHOWN = 2;
const DESCRIPTION_COLLAPSED_HEIGHT_PX = 112;
const VIEW_REGISTERED_EVENTS_TO = `${MYLEARNING_URL_FRAGMENT}?contentType=LIVE_EVENT`;

// @bend-expect trellis-tokenized
const DescriptionHtml = styled.div.withConfig({
  displayName: "LiveEventOverviewModalContainer__DescriptionHtml"
})(({
  theme
}) => ({
  'ul, ol': {
    margin: `${theme.space['200']} 0`,
    paddingInlineStart: theme.space['700']
  },
  ul: {
    listStyle: 'disc'
  },
  ol: {
    listStyle: 'decimal'
  },
  li: {
    marginBlockEnd: theme.space['100']
  },
  'strong, b': {
    fontWeight: 'bold'
  },
  'p:not(:last-child)': {
    marginBlockEnd: theme.space['200']
  }
}));

// `InstructorList` types `descriptions` as the lesson shape ({ english, ... }) but
// reads `descriptions[language.toLowerCase()]` at runtime, so our CHIRP short-code
// shape ({ en, fr, ... }) works as-is. The cast is scoped to that one field.
// Metadata + socialMediaLinks are omitted because they aren't read by the renderer
// and their CHIRP enum types don't line up with the lesson `Professor` shape.
const toInstructorListProfessor = p => {
  var _p$contentId;
  return {
    contentId: (_p$contentId = p.contentId) !== null && _p$contentId !== void 0 ? _p$contentId : p.id,
    id: p.id,
    name: p.name,
    image: p.image,
    jobTitle: p.jobTitle,
    organisation: p.organisation,
    organisationUrl: p.organisationUrl,
    updatedAt: p.updatedAt,
    descriptions: p.descriptions
  };
};
function EventInstructorList({
  handleOrganisationUrlClick,
  instructorIds,
  language
}) {
  const {
    professors,
    loading,
    error
  } = useEventInstructors(instructorIds);
  if (loading) {
    return /*#__PURE__*/_jsx(SkeletonWrapper, {
      borderRadius: "300",
      height: 312,
      marginBlockStart: "800",
      width: "100%"
    });
  }
  if (error) {
    Raven.captureException(error);
    return null;
  }
  if (professors.length === 0) {
    return null;
  }
  return /*#__PURE__*/_jsx(InstructorList, {
    handleOrganisationUrlClick: handleOrganisationUrlClick,
    language: language.toLowerCase(),
    professors: professors.map(toInstructorListProfessor)
  });
}
const LiveEventOverviewModalContainer = ({
  liveEventId,
  language
}) => {
  var _upcomingSessions$fin, _getPastSessions$, _priorityJoinableSess, _LANGUAGE_STRINGS$rea, _learningEvent$instru, _registeredSession$jo;
  const {
    isMobile
  } = useMediaQuery();
  const {
    learningEvent,
    sessions,
    loading,
    error
  } = useLearningEvent(liveEventId);
  const {
    recordingUrls,
    attended
  } = useEventRecording(liveEventId);
  const [showAllSessions, setShowAllSessions] = useState(false);
  const [registeredSession, setRegisteredSession] = useState(null);

  // Capture the just-registered session at click time (with the joinUrl from
  // the mutation response stamped on) rather than waiting for the GET_EVENT_BY_ID
  // refetch to surface it via `sessions.find`. This keeps the confirmation
  // modal independent of refetch timing and avoids treating `joinUrl` as a
  // stable identity key.
  const handleRegistered = (session, data) => {
    setRegisteredSession(Object.assign({}, session, {
      joinUrl: data.joinUrl
    }));
  };

  // Tick so the join window (sessions starting within 10 minutes) opens and
  // closes client-side as time passes, without waiting for a data refetch.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const intervalId = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(intervalId);
  }, []);
  const priorityJoinableSession = useMemo(() => getPriorityJoinableSession(sessions, now), [sessions, now]);
  const upcomingSessions = useMemo(() => getUpcomingSessionsNotStartingSoon(sessions, now), [sessions, now]);

  // A foregrounded session that hasn't actually started yet (the BE only flips
  // status to STARTED when the host begins) is "starting soon" — the join CTA
  // is available, but the copy shouldn't claim the session is live.
  const isPriorityJoinableSessionStartingSoon = !!priorityJoinableSession && priorityJoinableSession.status !== 'STARTED';
  const nextRegisteredUpcomingSession = (_upcomingSessions$fin = upcomingSessions.find(s => !!s.joinUrl)) !== null && _upcomingSessions$fin !== void 0 ? _upcomingSessions$fin : null;

  // The recording is event-level, so attribute the most recent past session as
  // its representative for analytics joins.
  const recordingSession = (_getPastSessions$ = getPastSessions(sessions)[0]) !== null && _getPastSessions$ !== void 0 ? _getPastSessions$ : null;
  const pastRecording = recordingUrls.length > 0 ? {
    attended,
    recordingUrls,
    sessionId: recordingSession === null || recordingSession === void 0 ? void 0 : recordingSession.id,
    startTime: recordingSession ? getSessionStartTime(recordingSession) : undefined
  } : null;
  const isPriorityJoinableSessionRegistered = !!(priorityJoinableSession !== null && priorityJoinableSession !== void 0 && priorityJoinableSession.joinUrl);
  const priorityJoinableSessionJoinUrl = (_priorityJoinableSess = priorityJoinableSession === null || priorityJoinableSession === void 0 ? void 0 : priorityJoinableSession.joinUrl) !== null && _priorityJoinableSess !== void 0 ? _priorityJoinableSess : null;
  const visibleUpcomingSessions = showAllSessions ? upcomingSessions : upcomingSessions.slice(0, INITIAL_SESSIONS_SHOWN);
  const getOrganisationUrlClickHandler = (professorId, organisation, organisationUrl) => () => {
    UsageTracker.trackStandalone('Learning Center Interaction', {
      action: 'professor-organisation-click',
      contentType: 'LIVE_EVENT',
      appearsIn: 'live-event-overview',
      organisation,
      organisationUrl,
      professorId,
      contentId: liveEventId,
      contentName: learningEvent === null || learningEvent === void 0 ? void 0 : learningEvent.name
    });
  };
  return /*#__PURE__*/_jsxs(OverviewContent, {
    assetLanguage: language,
    backgroundColor: "fill-surface-recessed",
    error: error,
    fallbackThumbnailUrl: learningEvent === null || learningEvent === void 0 ? void 0 : learningEvent.previewThumbnailRectangleUrl,
    loading: loading,
    reserveVideoAspectRatio: true,
    children: [/*#__PURE__*/_jsxs(View, {
      backgroundColor: "fill-surface-recessed",
      paddingInline: isMobile ? '300' : '800',
      children: [/*#__PURE__*/_jsx(LiveEventRegistrationAlert, {
        contentName: learningEvent === null || learningEvent === void 0 ? void 0 : learningEvent.name,
        hasRegisteredUpcoming: !!nextRegisteredUpcomingSession,
        isLiveSessionRegistered: isPriorityJoinableSessionRegistered,
        isStartingSoon: isPriorityJoinableSessionStartingSoon,
        joinUrl: priorityJoinableSessionJoinUrl,
        liveEventId: liveEventId,
        liveSession: priorityJoinableSession,
        onRegistered: handleRegistered,
        pastRecording: pastRecording,
        viewRegisteredEventsTo: VIEW_REGISTERED_EVENTS_TO
      }), /*#__PURE__*/_jsx(ModalTileSection, {
        padding: "0",
        paddingBlockStart: "500",
        children: /*#__PURE__*/_jsx(Text, {
          as: isMobile ? 'h4' : 'h2',
          marginBlockEnd: "400",
          typography: "heading-600",
          children: learningEvent === null || learningEvent === void 0 ? void 0 : learningEvent.name
        })
      }), /*#__PURE__*/_jsx(SectionHeading, {
        paddingBlockEnd: "400",
        paddingBlockStart: "800",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "academyComponents.liveEventOverviewModal.eventDetails"
        })
      }), /*#__PURE__*/_jsxs(CardWrapper, {
        marginBlockEnd: "0",
        children: [/*#__PURE__*/_jsx(ModalTileSection, {
          paddingBlockEnd: "600",
          children: /*#__PURE__*/_jsx(ExpandableText, {
            animate: false,
            buttonAlign: "left",
            collapseButtonText: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "academyComponents.liveEventOverviewModal.showLess"
            }),
            expandButtonText: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "academyComponents.liveEventOverviewModal.showMore"
            }),
            maxHeight: DESCRIPTION_COLLAPSED_HEIGHT_PX,
            children: /*#__PURE__*/_jsx(DescriptionHtml
            // nosemgrep: typescript.react.security.audit.react-dangerouslysetinnerhtml.react-dangerouslysetinnerhtml -- description is sanitized against the sanitize-text HTML allowlist (config.HTML) before rendering
            , {
              dangerouslySetInnerHTML: {
                __html: sanitize(learningEvent === null || learningEvent === void 0 ? void 0 : learningEvent.description, sanitizerConfig.HTML)
              },
              "data-test-id": "live-event-description"
            })
          })
        }), (learningEvent === null || learningEvent === void 0 ? void 0 : learningEvent.language) && /*#__PURE__*/_jsxs(ModalTileSection, {
          paddingBlockEnd: "400",
          children: [/*#__PURE__*/_jsx(H7, {
            paddingBlockEnd: "100",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "academyComponents.liveEventOverviewModal.languagesAvailable"
            })
          }), /*#__PURE__*/_jsx(Tag, {
            inline: true,
            margin: "0",
            children: (_LANGUAGE_STRINGS$rea = LANGUAGE_STRINGS[readUnhandledEnumCase(learningEvent.language).toLowerCase()]) !== null && _LANGUAGE_STRINGS$rea !== void 0 ? _LANGUAGE_STRINGS$rea : readUnhandledEnumCase(learningEvent.language).toUpperCase()
          })]
        })]
      }), /*#__PURE__*/_jsx(SectionHeading, {
        paddingBlockEnd: "400",
        paddingBlockStart: "800",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "academyComponents.liveEventOverviewModal.upcomingSessions"
        })
      }), upcomingSessions.length > 0 ? /*#__PURE__*/_jsxs(View, {
        children: [/*#__PURE__*/_jsx(Flex, {
          flexDirection: "column",
          children: visibleUpcomingSessions.map(session => /*#__PURE__*/_jsx(SessionCard, {
            contentName: learningEvent === null || learningEvent === void 0 ? void 0 : learningEvent.name,
            isRegistered: !!session.joinUrl,
            liveEventId: liveEventId,
            onRegistered: handleRegistered,
            session: session
          }, session.id))
        }), /*#__PURE__*/_jsx(Flex, {
          justifyContent: isMobile ? 'center' : 'start',
          children: (showAllSessions || visibleUpcomingSessions.length < upcomingSessions.length) && /*#__PURE__*/_jsx(Button, {
            "data-test-id": "live-event-sessions-toggle",
            onClick: () => setShowAllSessions(prev => !prev),
            responsive: false,
            size: "small",
            use: "transparent",
            children: showAllSessions ? /*#__PURE__*/_jsxs(_Fragment, {
              children: [/*#__PURE__*/_jsx(FormattedMessage, {
                message: "academyComponents.liveEventOverviewModal.showLess"
              }), ' ', /*#__PURE__*/_jsx(UpIcon, {})]
            }) : /*#__PURE__*/_jsxs(_Fragment, {
              children: [/*#__PURE__*/_jsx(FormattedMessage, {
                message: "academyComponents.liveEventOverviewModal.showMore"
              }), ' ', /*#__PURE__*/_jsx(DownIcon, {})]
            })
          })
        })]
      }) : /*#__PURE__*/_jsx(CardWrapper, {
        marginBlockEnd: "0",
        children: /*#__PURE__*/_jsx(ModalTileSection, {
          paddingBlockEnd: "600",
          children: /*#__PURE__*/_jsx(Text, {
            typography: "body-100",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "academyComponents.liveEventOverviewModal.noSessionsScheduled"
            })
          })
        })
      }), !!(learningEvent !== null && learningEvent !== void 0 && (_learningEvent$instru = learningEvent.instructorIds) !== null && _learningEvent$instru !== void 0 && _learningEvent$instru.length) && /*#__PURE__*/_jsx(EventInstructorList, {
        handleOrganisationUrlClick: getOrganisationUrlClickHandler,
        instructorIds: learningEvent.instructorIds,
        language: language
      }), /*#__PURE__*/_jsx(View, {
        paddingBlockEnd: "800"
      })]
    }), registeredSession && learningEvent && /*#__PURE__*/_jsx(RegistrationConfirmationModal, {
      isLive: (priorityJoinableSession === null || priorityJoinableSession === void 0 ? void 0 : priorityJoinableSession.id) === registeredSession.id,
      isStartingSoon: isPriorityJoinableSessionStartingSoon && (priorityJoinableSession === null || priorityJoinableSession === void 0 ? void 0 : priorityJoinableSession.id) === registeredSession.id,
      joinUrl: (_registeredSession$jo = registeredSession.joinUrl) !== null && _registeredSession$jo !== void 0 ? _registeredSession$jo : null,
      liveEventId: liveEventId,
      liveEventTitle: learningEvent.name,
      onClose: () => setRegisteredSession(null),
      session: registeredSession
    })]
  });
};
export default LiveEventOverviewModalContainer;